/* istanbul ignore file */
import React, { useMemo, useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import * as blueprint from '@adc/blueprint'
import configment from '@adc/configment'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Helmet from 'react-helmet'
import * as graph from '@adc/sdk'
import { standardSchemas } from '@adc/analytics'
import { safeStringify } from '@adc/core-utils/src/helpers/string'
import {
  TutorialTooltipActions,
  AuctionApplication,
  NullComponent,
  withApplicationNotificationsContext,
  hooks as adcHooks,
} from '@adc/ui-adc'
import { getDefaultNearbyRadius } from '../util/seekConversion'
import {
  getTitle,
  getDescription,
  getAddressKeyTerms,
} from '../converters/seoBuilder'
import auctionSmartAppImg from '../assets/auction_smart_banner_logo.svg'
import {
  getVirtualTitle,
  trackPageLoadWithFlags,
  trackVirtualSearchResults,
} from '../services/analytics'
import { fetchUserDetails } from '../actions/user'
import {
  closeLightbox,
  updateSavedSearch,
  savedSearchLookupForBlueprintSearch,
} from '../actions/savedSearch'
import { getMaxResultsAllowed } from '../util/maxResultsAllowed'
import {
  ASSETS_FETCH_START,
  ASSETS_FETCH_SUCCESS,
  ASSETS_TRACK_LATEST_SEARCH,
} from '../constants/actionTypes'
import { fetchAdContent as fetchAdContentAction } from '../actions/assets'
import SearchPanel from '../components/searchPanel'
import SearchHeaderWithNotifications from '../components/searchHeaderWithNotifications'
import SaveSearchModal from '../components/saveSearchModal'
import { getFrequentlyAskedQuestions } from '../util/seoContent'
import OnboardingDrawer from './onBoardingDrawer'

const BlueprintPoweredApp = ({
  user,
  featureFlags,
  initialized,
  incrementTutorial,
  assets,
  savedSearch,
  onAuthenticationComplete,
  notifications,
  marketingTagsConfig,
  storeCurrentSearchId,
  fetchAdContent,
  startFetchAssets,
  successFetchAssets,
  closeLightbox,
  updateSavedSearch,
  savedSearchLookupForBlueprintSearch,
  experiments,
}) => {
  const location = useLocation()
  const history = useHistory()
  const [currentLocation] = useState(configment.get('REQUEST_LOCATION', null))

  const { data, actions } = blueprint.search.hooks.useListingsSearch({
    url: `/residential${location.pathname}${location.search}`,
    currentLocation,
    marketingTagsConfig,
    user,
    featureFlags,
    maxResultsAllowed: getMaxResultsAllowed(featureFlags),
    defaultNearbySearchRadius: getDefaultNearbyRadius(featureFlags),
  })

  useEffect(() => {
    if (data.currentSearchId) {
      const fullUrl = `${window.location.origin}${data.currentUrl}`

      // Fetch adglare content before each search
      fetchAdContent(fullUrl)
    }
  }, [data.currentSearchId, data.currentUrl, fetchAdContent])

  useEffect(() => {
    if (data.isLoading && !assets.fetchProgress.loading) {
      startFetchAssets()
    }
  }, [
    data.isLoading,
    assets.fetchProgress.loading,
    startFetchAssets,
    fetchAdContent,
  ])

  useEffect(() => {
    if (!data.isLoading && assets.fetchProgress.loading) {
      const newAssets = {
        currentSearchId: data.currentSearchId,
        nearbyTotal: data.listings.nearbyTotal,
        total: data.listings.total_count || 0,
        offset: data.listings.offset || 0,
        assets: data.listings.content || [],
        propertyTypeTotals: data.listings.propertyTypeTotals,
        remoteBideTotals: data.listings.remoteBideTotals,
      }

      storeCurrentSearchId(data.currentSearchId)
      savedSearchLookupForBlueprintSearch(data.listingCompatabilityFilters)
      successFetchAssets(newAssets)

      const queryParams = {
        ...data.currentFilters,
      }

      trackVirtualSearchResults(
        data.currentUrl,
        newAssets.total,
        getVirtualTitle(queryParams)
      )
    }
  }, [
    startFetchAssets,
    assets.fetchProgress.loading,
    assets.currentSearchId,
    data.listings,
    data.currentSearchId,
    data.listingCompatabilityFilters,
    data.isLoading,
    storeCurrentSearchId,
    successFetchAssets,
    savedSearchLookupForBlueprintSearch,
    data.currentFilters,
    data.currentUrl,
  ])

  useEffect(() => {
    if (data.currentUrl) {
      const newCurrentUrl = data.currentUrl.replace(/^\/residential\/?/, '/')
      const currentUrl =
        window.location.pathname.replace(/^\/residential\/?/, '/') +
        window.location.search

      if (newCurrentUrl !== currentUrl) {
        history.push(newCurrentUrl)
      }
    }
  }, [data.currentUrl, history])

  adcHooks.useMountEffect(() => {
    // TODO: adc-fe is now 6 feet under *draws cross on chest*. Investigate where this can possibly be set from now we are all on the same
    // stack.
    //
    // signup via adc-fe needs transient message
    const url = window.location.search

    if (url.indexOf('signupReferer') !== -1) {
      notifications.overlayBanner.open({ title: 'Thank you for signing up.' })

      // TODO: This is a specific error code that the pdp will sent in the case of an error. Perhaps we can make this
      // morpheus property service.
      // a constant and get away from coupling it to an API defined error code, which won't mean anything with the
    } else if (url.indexOf('msgcode=PD-101') !== -1) {
      notifications.overlayBanner.open({
        title: 'This property is no longer available.',
        message: 'See similar properties nearby.',
      })
    }

    trackPageLoadWithFlags(user, featureFlags)
  })

  const singleFamilySchemas = assets.list
    .filter(
      listing =>
        listing?.primary_property?.summary.structure_type_group ===
        graph.enums.PropertyStructureTypeGroup.SingleFamily
    )
    .slice(0, 50)
    .map(a => {
      return standardSchemas.singleFamilyHomeSchema(a)
    })

  const ADC_LOGO = configment.get('AUCTIONCOM_LOGO_URL', '')
  const corporateSchema = useMemo(
    () => standardSchemas.corporateSchema(ADC_LOGO),
    [ADC_LOGO]
  )
  const searchSiteSchema = useMemo(() => standardSchemas.searchSiteSchema(), [])
  const organizationSchema = useMemo(
    () => standardSchemas.organizationSchema(ADC_LOGO),
    [ADC_LOGO]
  )

  const webpageSchema = useMemo(
    () =>
      standardSchemas.webPageSchema(
        'https://www.auction.com/residential',
        getTitle(
          data.currentFilters,
          featureFlags['fe.resi.seo.custom_title_tag']
        ),
        getDescription(
          data.currentFilters,
          featureFlags['fe.resi.seo.custom_meta_description_tag']
        ),
        [...getAddressKeyTerms(data.currentFilters)]
      ),
    [featureFlags, data.currentFilters]
  )

  const faqpageSchema = useMemo(
    () =>
      standardSchemas.faqPageSchema(
        'https://www.auction.com/residential',
        getTitle(
          data.currentFilters,
          featureFlags['fe.resi.seo.custom_title_tag']
        ),
        getDescription(
          data.currentFilters,
          featureFlags['fe.resi.seo.custom_meta_description_tag']
        ),
        [...getAddressKeyTerms(data.currentFilters)],
        getFrequentlyAskedQuestions(data.currentFilters, assets)
      ),
    [featureFlags, data.currentFilters, assets]
  )

  const helmetConfig = useMemo(
    () => ({
      title: getTitle(
        data.currentFilters,
        featureFlags['fe.resi.seo.custom_title_tag']
      ),
      description: getDescription(
        data.currentFilters,
        featureFlags['fe.resi.seo.custom_meta_description_tag']
      ),
      children: (
        <script type='application/ld+json'>
          {safeStringify({
            '@context': 'https://schema.org',
            '@graph': [
              corporateSchema,
              searchSiteSchema,
              organizationSchema,
              webpageSchema,
              faqpageSchema,
              ...singleFamilySchemas,
            ],
          })}
        </script>
      ),
    }),
    [
      corporateSchema,
      featureFlags,
      data.currentFilters,
      organizationSchema,
      searchSiteSchema,
      singleFamilySchemas,
      webpageSchema,
      faqpageSchema,
    ]
  )

  const capabilities = useMemo(
    () => ({
      accessibility: {
        equalweb: {
          alignHorizontal: 'right',
          alignVertical: 'middle',
        },
      },
      authentication: {
        enabled: true,
        options: {
          onAuthenticationComplete,
        },
      },
      surveyMonkey: {
        enabled: false,
      },
    }),
    [onAuthenticationComplete]
  )

  return (
    <AuctionApplication
      user={user}
      featureFlags={featureFlags}
      sspConfig={configment.get()}
      helmetConfig={helmetConfig}
      overridableComponents={{
        header: (
          <SearchHeaderWithNotifications
            user={user}
            featureFlags={featureFlags}
            filters={data.currentUiFilterGroups.locationFilterGroup}
            onFilterChange={actions.onFilterChange}
            onLocationSuggestionSelect={actions.onLocationSuggestionSelect}
          />
        ),
        footer: <NullComponent />,
      }}
      capabilities={capabilities}
      experiments={experiments}
    >
      <Helmet
        link={[
          { rel: 'android-touch-icon', href: auctionSmartAppImg },
          { rel: 'apple-touch-icon', href: auctionSmartAppImg },
        ]}
      />
      {initialized && (
        <OnboardingDrawer
          elmId='search_onboarding_drawer'
          user={user}
          startTour={incrementTutorial}
          featureFlags={featureFlags}
        />
      )}
      <SearchPanel
        isLoading={data.isLoading}
        listings={data.listings}
        currentFilters={data.currentFilters}
        uiFilterGroups={data.currentUiFilterGroups}
        listingCompatabilityFilters={data.listingCompatabilityFilters}
        defaultUiFilterGroups={data.defaultUiFilterGroups}
        onFilterChange={actions.onFilterChange}
      />

      {user.logged_in && (
        <>
          <SaveSearchModal
            savedSearch={savedSearch}
            currentFilters={data.currentFilters}
            listingCompatabilityFilters={data.listingCompatabilityFilters}
            closeLightbox={closeLightbox}
            updateSavedSearch={updateSavedSearch}
          />
        </>
      )}
    </AuctionApplication>
  )
}

BlueprintPoweredApp.propTypes = {
  initialized: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  featureFlags: PropTypes.object.isRequired,
  incrementTutorial: PropTypes.func.isRequired,
  assets: PropTypes.object.isRequired,
  savedSearch: PropTypes.object.isRequired,
  onAuthenticationComplete: PropTypes.func.isRequired,
  notifications: PropTypes.object.isRequired,
  marketingTagsConfig: PropTypes.object.isRequired,
  storeCurrentSearchId: PropTypes.object.isRequired,
  startFetchAssets: PropTypes.func.isRequired,
  successFetchAssets: PropTypes.func.isRequired,
  fetchAdContent: PropTypes.func.isRequired,
  closeLightbox: PropTypes.func.isRequired,
  updateSavedSearch: PropTypes.func.isRequired,
  savedSearchLookupForBlueprintSearch: PropTypes.func.isRequired,
  experiments: PropTypes.object,
}

const mapStateToProps = store => ({
  marketingTagsConfig: store.marketingTagsConfig,
  initialized: store.assets.initialized,
  featureFlags: store.featureFlags,
  user: store.user,
  assets: store.assets,
  savedSearch: store.savedSearch,
})

const mapActions = {
  incrementTutorial: TutorialTooltipActions.incrementTutorial,
  onAuthenticationComplete: ({ user }) => fetchUserDetails(user),
  storeCurrentSearchId: currentSearchId => dispatch =>
    dispatch({ type: ASSETS_TRACK_LATEST_SEARCH, currentSearchId }),
  startFetchAssets: () => dispatch => dispatch({ type: ASSETS_FETCH_START }),
  successFetchAssets: value => dispatch =>
    dispatch({ type: ASSETS_FETCH_SUCCESS, value }),
  fetchAdContent: url => dispatch => fetchAdContentAction(dispatch, url),
  closeLightbox,
  updateSavedSearch,
  savedSearchLookupForBlueprintSearch,
}

export default withApplicationNotificationsContext(
  connect(mapStateToProps, mapActions)(BlueprintPoweredApp)
)
